import {
    EuiButton,
    EuiButtonEmpty,
    EuiComboBox,
    EuiFlexGroup,
    EuiFlexItem,
    EuiModal,
    EuiModalBody,
    EuiModalFooter,
    EuiModalHeader,
    EuiModalHeaderTitle,
} from "@elastic/eui";
import { EuiComboBoxOptionOption } from "@elastic/eui/src/components/combo_box/types";
import React, { useEffect, useState } from "react";

import { useScreensQuery } from "../../../api/react-query/queries/screens";
import { CategoryAdvancedOptions, CategoryScreen, TableItem } from "../../../utils/types";
import { ScreenListItemEditor } from "./ScreenListItemEditor";
import { ScreensSummary } from "./ScreenSummary";

type InlineScreenEditorProps = {
    tableItem: TableItem;
    initialValue: CategoryScreen[];
    onSave: (value: CategoryScreen[]) => void;
    onEdit: (row: number, column: number) => void;
    selectedColumn: number;
    readonly?: boolean;
};

export const InlineScreenEditor = ({
    tableItem,
    initialValue,
    onSave,
    onEdit,
    selectedColumn,
    readonly = false,
}: InlineScreenEditorProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [screens, setScreens] = useState<CategoryScreen[]>(initialValue);
    const { screenOptions, isLoading } = useScreensQuery();

    useEffect(() => {
        setScreens(initialValue);
    }, [initialValue, selectedColumn]);

    const handleOrderChange = (screen_id: string, newOrder: number) => {
        setScreens((prev) => prev.map((s) => (s.screen_id === screen_id ? { ...s, order_number: newOrder } : s)));
    };

    const handleColumnChange = (screen_id: string, newColumn: number) => {
        setScreens((prev) => prev.map((s) => (s.screen_id === screen_id ? { ...s, column_number: newColumn } : s)));
    };

    function handleAdvancedOptionsChange(screen_id: string, options: CategoryAdvancedOptions) {
        setScreens((prev) =>
            prev.map((screen) => (screen.screen_id === screen_id ? { ...screen, advanced_options: options } : screen))
        );
    }
    const freeScreens = screenOptions.filter((option) => !screens.some((s) => s.screen_id === option.id));
    const freeScreenOptions = freeScreens.map(
        (option): EuiComboBoxOptionOption => ({
            label: option.name,
            value: option.id,
        })
    );

    const openModal = () => {
        if (!modalOpen) {
            onEdit(tableItem.index, selectedColumn);
            setModalOpen(true);
        }
    };
    const closeModal = () => {
        setModalOpen(false);
        setScreens(initialValue);
        onEdit(-1, -1);
    };

    const handleAdd = (selectedOptions: EuiComboBoxOptionOption[]) => {
        if (selectedOptions[0].value) {
            const newScreen: CategoryScreen = {
                screen_id: selectedOptions[0].value.toString(),
                order_number: 0,
                column_number: 0,
            };
            setScreens((prev) => [...prev, newScreen]);
        }
    };

    const handleDelete = (screen_id: string) => {
        setScreens((prev) => prev.filter((s) => s.screen_id !== screen_id));
    };

    const handleSave = () => {
        onSave(screens);
        console.log("screens", screens);
        onEdit(-1, -1);
        setModalOpen(false);
    };

    return !isLoading ? (
        <div className="inline-table-screen-editor">
            <ScreensSummary openModal={openModal} screens={screens} screenOptions={screenOptions} />
            {modalOpen && (
                <EuiModal onClose={closeModal}>
                    <EuiModalHeader>
                        <EuiModalHeaderTitle>Screens - {tableItem.value.name}</EuiModalHeaderTitle>
                    </EuiModalHeader>
                    <EuiModalBody>
                        <EuiFlexGroup direction="column" gutterSize="s">
                            {screens.map((screen) => (
                                <EuiFlexItem key={screen.screen_id}>
                                    <ScreenListItemEditor
                                        key={screen.screen_id}
                                        screen={screen}
                                        handleDelete={handleDelete}
                                        handleOrderChange={handleOrderChange}
                                        handleColumnChange={handleColumnChange}
                                        handleAdvancedOptionsChange={handleAdvancedOptionsChange}
                                        screenOptions={screenOptions}
                                    />
                                </EuiFlexItem>
                            ))}
                        </EuiFlexGroup>
                    </EuiModalBody>
                    <div style={{ padding: "24px" }}>
                        <EuiComboBox
                            singleSelection={{ asPlainText: true }}
                            placeholder={freeScreens.length > 0 ? "+ Add a screen" : "No free screens"}
                            options={freeScreenOptions}
                            onChange={handleAdd}
                            isDisabled={freeScreens.length === 0}
                            fullWidth
                        />
                    </div>
                    <EuiModalFooter>
                        <EuiButtonEmpty onClick={closeModal}>Cancel</EuiButtonEmpty>
                        <EuiButton fill onClick={handleSave}>
                            Save
                        </EuiButton>
                    </EuiModalFooter>
                </EuiModal>
            )}
        </div>
    ) : (
        <></>
    );
};
